/**
 * CheckoutItemsList - 주문 항목 목록
 *
 * 각 아이템의 이름/수량/가격을 표시하고,
 * 커스터마이즈 옵션을 토글로 접기/펼치기 가능.
 */
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { CartItem } from '@/types';
import { formatCurrency } from '@/utils';

interface CheckoutItemsListProps {
  items: CartItem[];
  optionToggle?: { show: string; hide: string };
}

export function CheckoutItemsList({ items, optionToggle }: CheckoutItemsListProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-[12px]">
      {items.map((item) => {
        const hasOptions = item.customizations && item.customizations.length > 0;
        const isExpanded = expandedIds.has(item.id);

        return (
          <div key={item.id}>
            <div className="flex items-center gap-[12px]">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-[56px] h-[56px] rounded-[8px] object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="w-[56px] h-[56px] rounded-[8px] bg-muted flex-shrink-0 flex items-center justify-center text-[20px]" aria-hidden="true">
                  🍽️
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-[8px]">
                  <span className="text-[14px] font-semibold text-foreground leading-tight">
                    {item.name}
                  </span>
                  <span className="text-[14px] font-semibold text-primary flex-shrink-0">
                    {formatCurrency(item.price * item.quantity, 'MXN')}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-[4px]">
                  <span className="text-[12px] text-muted-foreground">
                    x{item.quantity}
                  </span>
                  {hasOptions && (
                    <button
                      onClick={() => toggle(item.id)}
                      className="flex items-center gap-[2px] text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? (
                        <>{optionToggle?.hide ?? '옵션 접기'} <ChevronUp className="w-[12px] h-[12px]" /></>
                      ) : (
                        <>{optionToggle?.show ?? '옵션 보기'} <ChevronDown className="w-[12px] h-[12px]" /></>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
            {hasOptions && isExpanded && (
              <div className="ml-[68px] mt-[6px] text-[12px] text-muted-foreground">
                {item.customizations!.join(' · ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
